/**
 * Expense Tracker Component (Issue #679)
 *
 * Enables vendors to log and manage fair-related expenses for ROI calculation.
 * Supports multiple expense types and receipt uploads.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { expenseApi } from '../services/api';
import { confirmAsync } from '../utils/toast';
import SimplePagination from './SimplePagination';
import { addCurrency, formatCurrency, parseCurrencyInput, compareCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Type-safe error handling
import {
  VendorFairExpense,
  VendorFairExpenseCreate,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  PaginatedExpenseResponse
} from '../types';

interface ExpenseTrackerProps {
  vendorId: number;
  fairId: number;
  onExpenseAdded?: () => void;
}

export const ExpenseTracker: React.FC<ExpenseTrackerProps> = ({
  vendorId,
  fairId,
  onExpenseAdded
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expenses, setExpenses] = useState<VendorFairExpense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Pagination state (Issue #1180)
  // Initialize from URL query params with validation
  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page');
    if (!pageParam) return 1;

    const page = parseInt(pageParam, 10);
    // Validate: must be positive integer, reasonable max
    if (isNaN(page) || page < 1 || page > 10000) {
      console.warn(`Invalid page param: ${pageParam}, defaulting to 1`);
      return 1;
    }
    return page;
  });
  const [pageSize, setPageSize] = useState(() => {
    // Priority: URL param > localStorage > default (20)
    const sizeParam = searchParams.get('pageSize');
    const validSizes = [10, 20, 50, 100];

    if (sizeParam) {
      const size = parseInt(sizeParam, 10);
      if (!isNaN(size) && validSizes.includes(size)) {
        return size;
      }
      console.warn(`Invalid pageSize param: ${sizeParam}, checking localStorage`);
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem('vendor_expenses_pageSize');
      if (stored) {
        const size = parseInt(stored, 10);
        if (!isNaN(size) && validSizes.includes(size)) {
          return size;
        }
      }
    } catch (e: unknown) {
      // localStorage might be disabled
      console.warn('Failed to read from localStorage:', e);
    }

    return 20; // Default (Issue #1180)
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state (Issue #1160)
  const [sortByAmount, setSortByAmount] = useState(false);

  // Form state
  const [expenseType, setExpenseType] = useState<ExpenseType>('booth_fee');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');

  // Update URL query params when page or pageSize changes (Issue #1180)
  useEffect(() => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);

      if (currentPage > 1) {
        newParams.set('page', currentPage.toString());
      } else {
        newParams.delete('page');
      }

      if (pageSize !== 20) {
        newParams.set('pageSize', pageSize.toString());
      } else {
        newParams.delete('pageSize');
      }

      return newParams;
    }, { replace: true });
  }, [currentPage, pageSize, setSearchParams]);

  // Type guard for paginated response (Issue #768 code review)
  const isPaginatedExpenseResponse = (
    data: VendorFairExpense[] | PaginatedExpenseResponse
  ): data is PaginatedExpenseResponse => {
    return 'pagination' in data && typeof (data as PaginatedExpenseResponse).pagination === 'object';
  };

  // Load expenses with pagination (Issue #768)
  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await expenseApi.getExpenses(vendorId, fairId, currentPage, pageSize);

      // Handle paginated response using type guard
      if (isPaginatedExpenseResponse(data)) {
        setExpenses(data.items);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } else {
        // Backward compatible: non-paginated response
        setExpenses(data);
        setTotalPages(1);
        setTotalCount(data.length);
      }

      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading expenses:', apiError);
    } finally {
      setLoading(false);
    }
  }, [vendorId, fairId, currentPage, pageSize]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Issue #1160: Use parseCurrencyInput for precision-safe validation
    const parsedAmount = parseCurrencyInput(amount);
    if (!amount || parsedAmount === '0.00') {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      // Issue #1160: Send amount as string for Decimal precision (Issue #890)
      const expense: VendorFairExpenseCreate = {
        vendor_id: vendorId,
        fair_id: fairId,
        expense_type: expenseType,
        amount: parsedAmount, // Already string from parseCurrencyInput
        date,
        notes: notes || undefined,
        receipt_url: receiptUrl || undefined
      };

      await expenseApi.createExpense(vendorId, fairId, expense);

      // Reset form
      setAmount('');
      setNotes('');
      setReceiptUrl('');
      setShowForm(false);

      // Reload expenses (reset to page 1 if needed)
      if (currentPage !== 1) {
        setCurrentPage(1); // This will trigger useEffect to reload
      } else {
        await loadExpenses(); // Already on page 1, just reload
      }

      if (onExpenseAdded) {
        onExpenseAdded();
      }

      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error creating expense:', apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId: number) => {
    const confirmed = await confirmAsync('Are you sure you want to delete this expense?');
    if (!confirmed) return;

    try {
      setLoading(true);
      await expenseApi.deleteExpense(vendorId, expenseId);

      // Reload current page to get fresh data (Issue #768 code review - fix race condition)
      const data = await expenseApi.getExpenses(vendorId, fairId, currentPage, pageSize);

      // Check if current page is now empty AND we're not on page 1
      if (isPaginatedExpenseResponse(data) && data.items.length === 0 && currentPage > 1) {
        // Navigate to previous page
        setCurrentPage(currentPage - 1);
      } else {
        // Update state with fresh data using type guard
        if (isPaginatedExpenseResponse(data)) {
          setExpenses(data.items);
          setTotalPages(data.pagination.total_pages);
          setTotalCount(data.pagination.total_count);
        } else {
          setExpenses(data);
          setTotalPages(1);
          setTotalCount(data.length);
        }
      }

      if (onExpenseAdded) {
        onExpenseAdded();
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error deleting expense:', apiError);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when changing page size

    // Persist to localStorage for future sessions (Issue #1180)
    try {
      localStorage.setItem('vendor_expenses_pageSize', newPageSize.toString());
    } catch (e: unknown) {
      console.warn('Failed to save page size to localStorage:', e);
    }

    // Note: loadExpenses will be triggered by useEffect when currentPage changes
  };

  // Issue #1160: Use Decimal.js addCurrency for precision-safe addition
  const totalExpenses = addCurrency(...expenses.map(exp => exp.amount));

  // Issue #1160: Sort expenses by amount if sortByAmount is true
  const sortedExpenses = sortByAmount
    ? [...expenses].sort((a, b) => compareCurrency(b.amount, a.amount)) // Descending order
    : expenses;

  const handleSortToggle = () => {
    setSortByAmount(!sortByAmount);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Expense Tracker</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors"
          data-testid="add-expense-button"
        >
          {showForm ? 'Cancel' : '+ Add Expense'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add Expense Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expense Type *
              </label>
              <select
                value={expenseType}
                onChange={(e) => setExpenseType(e.target.value as ExpenseType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal"
                required
                data-testid="expense-category-select"
              >
                {Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount * ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal"
                placeholder="0.00"
                required
                data-testid="expense-amount-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt URL
              </label>
              <input
                type="url"
                value={receiptUrl}
                onChange={(e) => setReceiptUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal"
                placeholder="https://..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal"
                rows={2}
                placeholder="Optional notes..."
                data-testid="expense-description-input"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50"
              data-testid="save-expense-button"
            >
              {loading ? 'Saving...' : 'Save Expense'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Expenses List */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Expenses
            </h3>
            <button
              onClick={handleSortToggle}
              className="text-sm text-secondary hover:text-primary-dark underline"
              data-testid="sort-by-amount"
            >
              {sortByAmount ? 'Unsort' : 'Sort by Amount'}
            </button>
          </div>
          <div className="text-lg font-bold text-gray-900" data-testid="total-expenses">
            {formatCurrency(totalExpenses)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {loading && !showForm ? (
          <div className="text-center py-8 text-gray-500">Loading expenses...</div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No expenses recorded yet. Click "Add Expense" to get started.
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {sortedExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid="expense-list-item"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-900">
                          {EXPENSE_TYPE_LABELS[expense.expense_type]}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(expense.date).toLocaleDateString()}
                        </span>
                      </div>
                      {expense.notes && (
                        <p className="text-sm text-gray-600 mb-2">{expense.notes}</p>
                      )}
                      {expense.receipt_url && (
                        <a
                          href={expense.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-secondary hover:underline"
                        >
                          View Receipt
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-gray-900" data-testid="expense-amount">
                        {formatCurrency(expense.amount)} {/* Issue #905: Decimal.js formatting */}
                      </span>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination (Issue #1180) */}
            <SimplePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              disabled={loading}
              ariaLabel="Expense list pagination"
              pageSize={pageSize}
              pageSizeOptions={[10, 20, 50, 100]}
              onPageSizeChange={handlePageSizeChange}
              totalCount={totalCount}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ExpenseTracker;
